import React, { useState } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Button,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Grid,
  Alert,
  Box,
  Typography,
  IconButton,
  Divider,
  CircularProgress
} from '@mui/material';
import {
  Close as CloseIcon,
  Add as AddIcon
} from '@mui/icons-material';
import { useTranslation } from 'react-i18next';
import { useMutation } from '@tanstack/react-query';
import { useSnackbar } from 'notistack';

// Импорты сервисов и типов
import { api } from '../../services/api';
import { LocationPicker } from '../map';
import type { DeviceCreate, GeoLocation } from '../../types';

interface DeviceCreateDialogProps {
  open: boolean;
  onClose: () => void;
  onSuccess: (message: string) => void;
}

interface FormData {
  name: string;
  description: string;
  device_type: string;
  location: string;
  floor: string;
  room: string;
  device_uuid: string;
  ip_address: string;
  mac_address: string;
  hostname: string;
  os_version: string;
  player_version: string;
  screen_resolution: string;
  screen_orientation: 'landscape' | 'portrait';
  heartbeat_interval: number;
  
  // Геолокация
  geolocation: GeoLocation | null;
  country: string;
  city: string;
  full_address: string;
  location_id: string;
}

const initialFormData: FormData = {
  name: '',
  description: '',
  device_type: '',
  location: '',
  floor: '',
  room: '',
  device_uuid: '',
  ip_address: '',
  mac_address: '',
  hostname: '',
  os_version: '',
  player_version: '',
  screen_resolution: '',
  screen_orientation: 'landscape',
  heartbeat_interval: 60,
  
  // Геолокация
  geolocation: null,
  country: '',
  city: '',
  full_address: '',
  location_id: '',
};

const deviceTypes = [
  { value: 'raspberry_pi', label: 'Raspberry Pi' },
  { value: 'intel_nuc', label: 'Intel NUC' },
  { value: 'desktop_pc', label: 'Desktop PC' },
  { value: 'android_tv', label: 'Android TV' },
  { value: 'other', label: 'Other' },
];

const screenResolutions = [
  '1920x1080',
  '1366x768',
  '1280x1024',
  '1280x720',
  '1024x768',
  '3840x2160',
  '2560x1440',
];

export const DeviceCreateDialog: React.FC<DeviceCreateDialogProps> = ({
  open,
  onClose,
  onSuccess,
}) => {
  const { t } = useTranslation();
  const { enqueueSnackbar } = useSnackbar();
  const [formData, setFormData] = useState<FormData>(initialFormData);
  const [errors, setErrors] = useState<Record<string, string>>({});

  // Мутация для создания устройства
  const createDeviceMutation = useMutation({
    mutationFn: (deviceData: DeviceCreate) => api.createDevice(deviceData),
    onSuccess: (data) => {
      onSuccess(t('devices.deviceCreated', { name: data.name }));
      handleClose();
    },
    onError: (error: any) => {
      enqueueSnackbar(
        error?.response?.data?.detail || t('devices.errorCreatingDevice'),
        { variant: 'error' }
      );
    },
  });

  // Обработчик изменения формы
  const handleChange = (field: keyof FormData) => (
    event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement> | any
  ) => {
    const value = event.target.value;
    setFormData(prev => ({ ...prev, [field]: value }));
    
    // Очистка ошибки при изменении поля
    if (errors[field]) {
      setErrors(prev => ({ ...prev, [field]: '' }));
    }
  };

  // Обработчик изменения геолокации
  const handleGeolocationChange = (location: GeoLocation | null) => {
    setFormData(prev => ({ 
      ...prev, 
      geolocation: location,
      // Синхронизация с отдельными полями
      country: location?.country || '',
      city: location?.city || '',
      full_address: location?.full_address || '',
      location_id: location?.location_id || ''
    }));
  };

  // Валидация формы
  const validateForm = (): boolean => {
    const newErrors: Record<string, string> = {};

    if (!formData.name.trim()) {
      newErrors.name = t('validation.required');
    }

    if (!formData.device_type) {
      newErrors.device_type = t('validation.required');
    }

    if (!formData.device_uuid.trim()) {
      newErrors.device_uuid = t('validation.required');
    } else if (formData.device_uuid.length !== 36) {
      newErrors.device_uuid = t('validation.invalidUuid');
    }

    if (formData.ip_address && !/^(?:[0-9]{1,3}\.){3}[0-9]{1,3}$/.test(formData.ip_address)) {
      newErrors.ip_address = t('validation.invalidIpAddress');
    }

    if (formData.mac_address && !/^([0-9A-Fa-f]{2}[:-]){5}([0-9A-Fa-f]{2})$/.test(formData.mac_address)) {
      newErrors.mac_address = t('validation.invalidMacAddress');
    }

    if (formData.heartbeat_interval < 10 || formData.heartbeat_interval > 300) {
      newErrors.heartbeat_interval = t('validation.heartbeatInterval');
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  // Обработчик отправки формы
  const handleSubmit = () => {
    if (!validateForm()) {
      return;
    }

    const deviceData: DeviceCreate = {
      name: formData.name.trim(),
      description: formData.description.trim() || undefined,
      device_type: formData.device_type as any,
      location: formData.location.trim() || undefined,
      floor: formData.floor.trim() || undefined,
      room: formData.room.trim() || undefined,
      device_uuid: formData.device_uuid.trim(),
      ip_address: formData.ip_address.trim() || undefined,
      mac_address: formData.mac_address.trim() || undefined,
      hostname: formData.hostname.trim() || undefined,
      os_version: formData.os_version.trim() || undefined,
      player_version: formData.player_version.trim() || undefined,
      screen_resolution: formData.screen_resolution || undefined,
      screen_orientation: formData.screen_orientation,
      
      // Геолокация
      latitude: formData.geolocation?.latitude,
      longitude: formData.geolocation?.longitude,
      country: formData.geolocation?.country || formData.country.trim() || undefined,
      city: formData.geolocation?.city || formData.city.trim() || undefined,
      full_address: formData.geolocation?.full_address || formData.full_address.trim() || undefined,
      location_id: formData.geolocation?.location_id || formData.location_id.trim() || undefined,
      heartbeat_interval: formData.heartbeat_interval,
    };

    createDeviceMutation.mutate(deviceData);
  };

  // Обработчик закрытия диалога
  const handleClose = () => {
    setFormData(initialFormData);
    setErrors({});
    onClose();
  };

  // Генерация UUID
  const generateUUID = () => {
    const uuid = 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, function(c) {
      const r = Math.random() * 16 | 0;
      const v = c === 'x' ? r : (r & 0x3 | 0x8);
      return v.toString(16);
    });
    setFormData(prev => ({ ...prev, device_uuid: uuid }));
  };

  return (
    <Dialog 
      open={open} 
      onClose={handleClose}
      maxWidth="md"
      fullWidth
      PaperProps={{
        sx: { minHeight: '80vh' }
      }}
    >
      <DialogTitle>
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <Typography variant="h6" component="div">
            <AddIcon sx={{ mr: 1, verticalAlign: 'middle' }} />
            {t('devices.createDevice')}
          </Typography>
          <IconButton onClick={handleClose}>
            <CloseIcon />
          </IconButton>
        </Box>
      </DialogTitle>

      <DialogContent dividers>
        <Grid container spacing={3}>
          {/* Основная информация */}
          <Grid item xs={12}>
            <Typography variant="subtitle1" gutterBottom color="primary">
              {t('devices.basicInfo')}
            </Typography>
            <Divider sx={{ mb: 2 }} />
          </Grid>

          <Grid item xs={12} md={6}>
            <TextField
              fullWidth
              label={t('devices.name')}
              value={formData.name}
              onChange={handleChange('name')}
              error={!!errors.name}
              helperText={errors.name}
              required
            />
          </Grid>

          <Grid item xs={12} md={6}>
            <FormControl fullWidth error={!!errors.device_type} required>
              <InputLabel>{t('devices.type')}</InputLabel>
              <Select
                value={formData.device_type}
                label={t('devices.type')}
                onChange={handleChange('device_type')}
              >
                {deviceTypes.map((type) => (
                  <MenuItem key={type.value} value={type.value}>
                    {type.label}
                  </MenuItem>
                ))}
              </Select>
              {errors.device_type && (
                <Typography variant="caption" color="error" sx={{ mt: 0.5, ml: 2 }}>
                  {errors.device_type}
                </Typography>
              )}
            </FormControl>
          </Grid>

          <Grid item xs={12}>
            <TextField
              fullWidth
              label={t('devices.description')}
              value={formData.description}
              onChange={handleChange('description')}
              multiline
              rows={2}
            />
          </Grid>

          {/* Местоположение */}
          <Grid item xs={12}>
            <Typography variant="subtitle1" gutterBottom color="primary" sx={{ mt: 2 }}>
              {t('devices.location')}
            </Typography>
            <Divider sx={{ mb: 2 }} />
          </Grid>

          <Grid item xs={12} md={4}>
            <TextField
              fullWidth
              label={t('devices.location')}
              value={formData.location}
              onChange={handleChange('location')}
            />
          </Grid>

          <Grid item xs={12} md={4}>
            <TextField
              fullWidth
              label={t('devices.floor')}
              value={formData.floor}
              onChange={handleChange('floor')}
            />
          </Grid>

          <Grid item xs={12} md={4}>
            <TextField
              fullWidth
              label={t('devices.room')}
              value={formData.room}
              onChange={handleChange('room')}
            />
          </Grid>

          {/* Геолокация */}
          <Grid item xs={12}>
            <Typography variant="subtitle2" gutterBottom color="primary" sx={{ mt: 1 }}>
              {t('map.geolocation')}
            </Typography>
          </Grid>

          <Grid item xs={12} md={8}>
            <LocationPicker
              value={formData.geolocation}
              onChange={handleGeolocationChange}
              label={t('map.deviceLocation')}
              placeholder={t('map.selectOnMap')}
            />
          </Grid>

          <Grid item xs={12} md={4}>
            <TextField
              fullWidth
              label={t('map.locationId')}
              value={formData.location_id}
              onChange={handleChange('location_id')}
              helperText={t('devices.locationIdHelper')}
            />
          </Grid>

          {/* Дополнительные поля адреса для ручного ввода */}
          <Grid item xs={12} md={6}>
            <TextField
              fullWidth
              label={t('map.country')}
              value={formData.country}
              onChange={handleChange('country')}
            />
          </Grid>

          <Grid item xs={12} md={6}>
            <TextField
              fullWidth
              label={t('map.city')}
              value={formData.city}
              onChange={handleChange('city')}
            />
          </Grid>

          <Grid item xs={12}>
            <TextField
              fullWidth
              label={t('map.fullAddress')}
              value={formData.full_address}
              onChange={handleChange('full_address')}
              multiline
              rows={2}
            />
          </Grid>

          {/* Техническая информация */}
          <Grid item xs={12}>
            <Typography variant="subtitle1" gutterBottom color="primary" sx={{ mt: 2 }}>
              {t('devices.technicalInfo')}
            </Typography>
            <Divider sx={{ mb: 2 }} />
          </Grid>

          <Grid item xs={12} md={8}>
            <TextField
              fullWidth
              label={t('devices.deviceUuid')}
              value={formData.device_uuid}
              onChange={handleChange('device_uuid')}
              error={!!errors.device_uuid}
              helperText={errors.device_uuid || t('devices.deviceUuidHelper')}
              required
            />
          </Grid>

          <Grid item xs={12} md={4}>
            <Button
              fullWidth
              variant="outlined"
              onClick={generateUUID}
              sx={{ height: 56 }}
            >
              {t('devices.generateUuid')}
            </Button>
          </Grid>

          <Grid item xs={12} md={6}>
            <TextField
              fullWidth
              label={t('devices.ipAddress')}
              value={formData.ip_address}
              onChange={handleChange('ip_address')}
              error={!!errors.ip_address}
              helperText={errors.ip_address}
              placeholder="192.168.1.100"
            />
          </Grid>

          <Grid item xs={12} md={6}>
            <TextField
              fullWidth
              label={t('devices.macAddress')}
              value={formData.mac_address}
              onChange={handleChange('mac_address')}
              error={!!errors.mac_address}
              helperText={errors.mac_address}
              placeholder="AA:BB:CC:DD:EE:FF"
            />
          </Grid>

          <Grid item xs={12} md={6}>
            <TextField
              fullWidth
              label={t('devices.hostname')}
              value={formData.hostname}
              onChange={handleChange('hostname')}
            />
          </Grid>

          <Grid item xs={12} md={6}>
            <TextField
              fullWidth
              label={t('devices.osVersion')}
              value={formData.os_version}
              onChange={handleChange('os_version')}
              placeholder="Ubuntu 22.04"
            />
          </Grid>

          <Grid item xs={12} md={6}>
            <TextField
              fullWidth
              label={t('devices.playerVersion')}
              value={formData.player_version}
              onChange={handleChange('player_version')}
              placeholder="1.0.0"
            />
          </Grid>

          <Grid item xs={12} md={6}>
            <FormControl fullWidth>
              <InputLabel>{t('devices.screenResolution')}</InputLabel>
              <Select
                value={formData.screen_resolution}
                label={t('devices.screenResolution')}
                onChange={handleChange('screen_resolution')}
              >
                <MenuItem value="">
                  <em>{t('common.none')}</em>
                </MenuItem>
                {screenResolutions.map((resolution) => (
                  <MenuItem key={resolution} value={resolution}>
                    {resolution}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>

          <Grid item xs={12} md={6}>
            <FormControl fullWidth>
              <InputLabel>{t('devices.screenOrientation')}</InputLabel>
              <Select
                value={formData.screen_orientation}
                label={t('devices.screenOrientation')}
                onChange={handleChange('screen_orientation')}
              >
                <MenuItem value="landscape">{t('devices.orientation.landscape')}</MenuItem>
                <MenuItem value="portrait">{t('devices.orientation.portrait')}</MenuItem>
              </Select>
            </FormControl>
          </Grid>

          <Grid item xs={12} md={6}>
            <TextField
              fullWidth
              label={t('devices.heartbeatInterval')}
              type="number"
              value={formData.heartbeat_interval}
              onChange={handleChange('heartbeat_interval')}
              error={!!errors.heartbeat_interval}
              helperText={errors.heartbeat_interval || t('devices.heartbeatIntervalHelper')}
              InputProps={{
                endAdornment: <Typography variant="body2" color="text.secondary">{t('common.seconds')}</Typography>
              }}
            />
          </Grid>
        </Grid>

        {createDeviceMutation.isError && (
          <Alert severity="error" sx={{ mt: 2 }}>
            {createDeviceMutation.error?.message || t('devices.errorCreatingDevice')}
          </Alert>
        )}
      </DialogContent>

      <DialogActions sx={{ p: 3 }}>
        <Button onClick={handleClose} disabled={createDeviceMutation.isPending}>
          {t('common.cancel')}
        </Button>
        <Button
          variant="contained"
          onClick={handleSubmit}
          disabled={createDeviceMutation.isPending}
          startIcon={createDeviceMutation.isPending ? <CircularProgress size={16} /> : <AddIcon />}
        >
          {createDeviceMutation.isPending ? t('common.creating') : t('devices.createDevice')}
        </Button>
      </DialogActions>
    </Dialog>
  );
}; 